import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Clock, MessageCircle, Home, CalendarX2, BadgeCheck, Wallet } from "lucide-react";
import { format, parseISO } from "date-fns";
import { StayHeader } from "@/components/stays/StayHeader";
import { PropertyRules } from "@/components/stays/PropertyRules";
import Footer from "@/components/Footer";
import { getPaymentStatus, getBooking, getRules, fmtAmount } from "@/lib/stayflowApi";
import { cancellationLines, fmtTime } from "@/lib/propertyRules";
import { invoiceeLine } from "@/lib/gstBilling";
import { setMeta } from "@/lib/seo";

const MAX_POLLS = 100; // 100 × 3s ≈ 5 min
const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "";

const fmtDate = (s) => { try { return format(parseISO(s), "dd MMM yyyy"); } catch { return s; } };

const BookingStatus = () => {
  const { id } = useParams();
  const [polls, setPolls] = useState(0);
  const stopped = useRef(false);

  useEffect(() => { setMeta({ title: "Booking status — StayFlow" }); }, []);

  const local = useMemo(() => {
    try { const s = JSON.parse(localStorage.getItem("sf_last_booking") || "null"); if (s?.id === id) return s; } catch { /* ignore */ }
    return {};
  }, [id]);
  const localPhone = local.phone || "";

  const statusQ = useQuery({
    queryKey: ["payment-status", id],
    queryFn: () => getPaymentStatus(id),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.payment_status === "paid" || stopped.current) return false;
      return 3000;
    },
    retry: 2,
  });

  const bookingQ = useQuery({
    queryKey: ["booking", id],
    queryFn: () => getBooking(id, { guest_phone: localPhone }),
    enabled: !!localPhone,
    retry: 1,
  });

  useEffect(() => {
    if (statusQ.isFetched) setPolls((p) => p + 1);
  }, [statusQ.dataUpdatedAt, statusQ.isFetched]);

  const paid = statusQ.data?.payment_status === "paid";
  const link = statusQ.data?.payment_link_url;
  const exhausted = polls >= MAX_POLLS && !paid;
  if (paid || exhausted) stopped.current = true;
  // Booking exists but the server hasn't produced the Razorpay link yet.
  const awaitingLink = !statusQ.isLoading && !paid && !link && ["pending_payment", "pending"].includes(statusQ.data?.status || "pending_payment");
  const timedOut = exhausted && !awaitingLink;

  const b = bookingQ.data || {};
  // The amount the guest actually paid / will pay — always from the server.
  const paidAmount = statusQ.data?.amount_paid;
  const payable = statusQ.data?.amount_payable ?? b.amount_payable;
  const deliveryPhone = b.guest_phone || localPhone || "";
  const deliveryEmail = b.guest_email || local.email || "";

  const propertyId = b.property_id || b.business_id || statusQ.data?.property_id || local.property_id;
  const rulesQ = useQuery({ queryKey: ["rules", propertyId], queryFn: () => getRules(propertyId), enabled: !!propertyId, retry: 1 });
  const rules = rulesQ.data?.recorded === false ? null : rulesQ.data || null;

  // The booking's own terms (exact cut-off for these dates) come from the server.
  const src = { ...local, ...b, ...(statusQ.data || {}) };
  const cancelLines = cancellationLines(src.cancellation_terms);
  const checkIn = b.check_in || local.check_in;
  const checkOut = b.check_out || local.check_out;
  const checkInFrom = rules?.check_in_from || src.check_in_from;
  const checkOutBy = rules?.check_out_by || src.check_out_by;
  // Rules endpoint is the source of truth; the booking record is the fallback
  // when we can't tell which property this booking belongs to.
  const idNeeded = rules ? rules.id_required_at_checkin?.value === true : src.id_required_at_checkin === true;
  const depositNeeded = rules ? rules.security_deposit_required?.value === true : src.security_deposit_required === true;

  // B2B recipient: from the booking record, else what this browser sent at checkout.
  const invoicee = useMemo(
    () => invoiceeLine(b.bill_to_company || local.bill_to_company, b.bill_to_gstin || local.bill_to_gstin),
    [b.bill_to_company, b.bill_to_gstin, local]
  );

  const waLink = useMemo(() => {
    const msg = encodeURIComponent(`Hi StayFlow, regarding my booking ${id}`);
    return WHATSAPP ? `https://wa.me/${WHATSAPP}?text=${msg}` : `https://wa.me/?text=${msg}`;
  }, [id]);

  const Summary = () => (
    <>
    <div className="mt-6 rounded-2xl bg-paper-muted p-5 text-left text-sm space-y-2" data-testid="booking-summary">
      <div className="flex justify-between"><span className="text-ink-soft">Booking ID</span><span className="font-mono text-ink">{id}</span></div>
      {checkIn && <div className="flex justify-between"><span className="text-ink-soft">Check-in</span><span className="text-ink font-medium" data-testid="booking-checkin">{fmtDate(checkIn)}{checkInFrom ? ` · from ${fmtTime(checkInFrom)}` : ""}</span></div>}
      {checkOut && <div className="flex justify-between"><span className="text-ink-soft">Check-out</span><span className="text-ink font-medium" data-testid="booking-checkout">{fmtDate(checkOut)}{checkOutBy ? ` · by ${fmtTime(checkOutBy)}` : ""}</span></div>}
      {paid
        ? paidAmount != null && <div className="flex justify-between"><span className="text-ink-soft">Amount paid</span><span className="font-heading font-bold text-ink" data-testid="booking-amount-paid">{fmtAmount(paidAmount)}</span></div>
        : payable != null && <div className="flex justify-between"><span className="text-ink-soft">Total</span><span className="font-heading font-bold text-ink" data-testid="booking-amount-payable">{fmtAmount(payable)}</span></div>}
      {invoicee && <p className="pt-2 border-t border-black/5 text-xs text-ink-soft" data-testid="booking-invoicee">{invoicee}</p>}
    </div>

    {(deliveryEmail || deliveryPhone) && (
      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-left" data-testid="booking-delivery">
        <MessageCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
        <p className="text-sm text-ink-soft">
          <span className="font-semibold text-ink">
            Your booking details and private link {paid ? "have been sent" : "go"} to {deliveryEmail || "your email"}.
          </span>
          {deliveryPhone ? ` We've also sent a confirmation to ${deliveryPhone} on WhatsApp.` : ""}
        </p>
      </div>
    )}

    {(cancelLines.length > 0 || idNeeded || depositNeeded) && (
      <div className="mt-4 rounded-2xl border border-black/8 bg-white p-5 text-left space-y-3" data-testid="booking-terms">
        {cancelLines.length > 0 && (
          <div className="flex items-start gap-3">
            <CalendarX2 className="w-4 h-4 text-brand mt-0.5 shrink-0" aria-hidden="true" />
            <div className="text-sm text-ink-soft space-y-0.5" data-testid="booking-cancellation-terms">
              <p className="font-semibold text-ink">Cancellation</p>
              {cancelLines.map((l) => <p key={l}>{l}</p>)}
            </div>
          </div>
        )}
        {idNeeded && (
          <p className="flex items-start gap-3 text-sm text-ink-soft" data-testid="booking-id-required">
            <BadgeCheck className="w-4 h-4 text-brand mt-0.5 shrink-0" aria-hidden="true" />
            Photo ID required at check-in{rules?.id_required_at_checkin?.note ? ` — ${rules.id_required_at_checkin.note}` : ""}
          </p>
        )}
        {depositNeeded && (
          <p className="flex items-start gap-3 text-sm text-ink-soft" data-testid="booking-deposit-required">
            <Wallet className="w-4 h-4 text-brand mt-0.5 shrink-0" aria-hidden="true" />
            Security deposit required{rules?.security_deposit_required?.note ? ` — ${rules.security_deposit_required.note}` : ""}
          </p>
        )}
      </div>
    )}

    <div className="text-left">
      <PropertyRules rules={rules} testid="booking-property-rules" />
    </div>
    </>
  );

  return (
    <div className="bg-paper min-h-screen" data-testid="booking-status-page">
      <StayHeader />
      <main className="max-w-lg mx-auto px-5 py-16 md:py-24 text-center">
        {statusQ.isLoading && (
          <div data-testid="status-loading"><Loader2 className="w-10 h-10 mx-auto text-brand animate-spin" /><p className="mt-4 text-ink-soft">Checking your payment…</p></div>
        )}

        {!statusQ.isLoading && paid && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} data-testid="status-paid">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-11 h-11 text-green-600" /></div>
            <h1 className="mt-6 font-heading font-extrabold text-3xl tracking-tight text-ink">Booking confirmed!</h1>
            <p className="mt-3 text-ink-soft" data-testid="booking-paid-note">
              Your payment was successful. Your booking details and private link have been sent to {deliveryEmail || "your email"}.
              {deliveryPhone ? ` We've also sent a confirmation to ${deliveryPhone} on WhatsApp.` : ""}
            </p>
            <Summary />
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={waLink} target="_blank" rel="noreferrer" data-testid="whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-medium px-6 h-12"><MessageCircle className="w-5 h-5" /> WhatsApp us</a>
              <Link to="/stays" className="inline-flex items-center justify-center gap-2 rounded-full border border-ink text-ink font-medium px-6 h-12"><Home className="w-5 h-5" /> Explore more stays</Link>
            </div>
          </motion.div>
        )}

        {awaitingLink && (
          <div data-testid="status-awaiting-link">
            <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center"><Clock className="w-10 h-10 text-amber-600" /></div>
            <h1 className="mt-6 font-heading font-extrabold text-2xl tracking-tight text-ink">Your room is held</h1>
            <p className="mt-3 text-ink-soft" data-testid="payment-message">{src.payment_message || "Your secure payment link isn't ready yet. Please try again in a moment."}</p>
            <Summary />
            <button type="button" onClick={() => statusQ.refetch()} disabled={statusQ.isFetching} data-testid="payment-try-again"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-medium px-6 h-12 disabled:opacity-60">
              {statusQ.isFetching ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</> : "Try again"}
            </button>
          </div>
        )}

        {!statusQ.isLoading && !paid && !timedOut && !awaitingLink && (
          <div data-testid="status-pending">
            <div className="w-20 h-20 mx-auto rounded-full bg-brand-soft flex items-center justify-center"><Loader2 className="w-10 h-10 text-brand animate-spin" /></div>
            <h1 className="mt-6 font-heading font-extrabold text-2xl tracking-tight text-ink">Confirming your payment…</h1>
            <p className="mt-3 text-ink-soft">If you haven't paid yet, use Resume payment below. This page updates automatically once Razorpay confirms.</p>
            <Summary />
            {link && (
              <a href={link} data-testid="resume-payment" className="mt-6 inline-flex rounded-full bg-brand text-white font-medium px-6 h-12 items-center">Resume payment</a>
            )}
          </div>
        )}

        {!statusQ.isLoading && timedOut && (
          <div data-testid="status-timeout">
            <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center"><Clock className="w-10 h-10 text-amber-600" /></div>
            <h1 className="mt-6 font-heading font-extrabold text-2xl tracking-tight text-ink">We're confirming your payment</h1>
            <p className="mt-3 text-ink-soft">You'll receive a WhatsApp confirmation shortly. If money was deducted, your booking is safe.</p>
            <Summary />
            <a href={waLink} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-medium px-6 h-12"><MessageCircle className="w-5 h-5" /> Contact us on WhatsApp</a>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BookingStatus;
